import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  SESSION_INACTIVITY_TIMEOUT_MS,
  clearLastActivityAt,
  isSessionInactive,
  readLastActivityAt,
  writeLastActivityAt,
} from '../../utils/sessionInactivity';

const CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 10 * 1000;

const InactivityLogoutGuard: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggingOutRef = React.useRef(false);
  const lastWriteRef = React.useRef(0);

  React.useEffect(() => {
    if (!isAuthenticated) {
      clearLastActivityAt();
      isLoggingOutRef.current = false;
      return;
    }

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
      writeLastActivityAt(now);
      lastWriteRef.current = now;
    };

    const maybeLogoutForInactivity = async () => {
      if (isLoggingOutRef.current) return;
      const lastActivityAt = readLastActivityAt();
      if (!isSessionInactive(lastActivityAt, Date.now(), SESSION_INACTIVITY_TIMEOUT_MS)) return;

      isLoggingOutRef.current = true;
      try {
        await logout();
      } catch {
        // Force logout path still clears state.
      } finally {
        clearLastActivityAt();
        if (!location.pathname.includes('/login')) {
          navigate('/login', { replace: true });
        }
      }
    };

    // Initialize activity timestamp for newly authenticated sessions.
    if (!readLastActivityAt()) {
      writeLastActivityAt(Date.now());
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markActivity();
      }
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityChange);

    const intervalId = window.setInterval(() => {
      void maybeLogoutForInactivity();
    }, CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, logout, navigate, location.pathname]);

  return null;
};

export default InactivityLogoutGuard;

